import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

const SECTION_IDS = ['about', 'services', 'process', 'stats', 'why-us', 'contact'];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ── Animated counter hook ── */
function useCountUp(end, duration = 2000) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  const start = useCallback(() => setStarted(true), []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { start(); obs.disconnect(); } },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [start]);

  useEffect(() => {
    if (!started) return;
    if (prefersReducedMotion()) { setCount(end); return; }
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, end, duration]);

  return [ref, count];
}

function StatItem({ value, suffix = '', label }) {
  const [ref, count] = useCountUp(value);
  return (
    <div className="stat" ref={ref}>
      <span className="stat-number">{count}{suffix}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

/* ── Scroll reveal hook ── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('revealed');
          obs.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ── Scroll position + page progress ── */
function useScrollState(threshold = 20) {
  const [state, setState] = useState({ scrolled: false, progress: 0 });
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setState({
        scrolled: window.scrollY > threshold,
        progress: max > 0 ? Math.min(window.scrollY / max, 1) : 0,
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(measure); };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [threshold]);
  return state;
}

/* ── Which section the reader is currently in ── */
function useActiveSection(ids) {
  const [active, setActive] = useState('');
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      const line = window.innerHeight * 0.35;
      let current = '';
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) current = id;
      });
      setActive(current);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(measure); };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ids]);
  return active;
}

/* ── Cursor spotlight on cards ── */
const trackSpotlight = (e) => {
  const el = e.currentTarget;
  const rect = el.getBoundingClientRect();
  el.style.setProperty('--mx', `${e.clientX - rect.left}px`);
  el.style.setProperty('--my', `${e.clientY - rect.top}px`);
};

/* ── Content ── */
const SERVICES = [
  {
    accent: '79, 143, 255',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    ),
    title: 'IT-konsulting',
    desc: 'Strategisk rådgivning och teknisk expertis som hjälper er att fatta rätt beslut kring IT-investeringar och digital transformation.',
  },
  {
    accent: '168, 85, 247',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
      </svg>
    ),
    title: 'Systemintegration',
    desc: 'Vi kopplar samman era system och applikationer för sömlöst dataflöde, effektivare processer och bättre insikter.',
  },
  {
    accent: '34, 211, 238',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9z" />
      </svg>
    ),
    title: 'Molnlösningar',
    desc: 'Migrering, optimering och förvaltning av molntjänster. Vi hjälper er utnyttja molnets fulla potential säkert och kostnadseffektivt.',
  },
  {
    accent: '52, 211, 153',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    title: 'Cybersäkerhet',
    desc: 'Skydda er verksamhet med proaktiv säkerhetsövervakning, penetrationstester och incidenthantering som håller hoten borta.',
  },
  {
    accent: '251, 191, 36',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      </svg>
    ),
    title: 'Support & drift',
    desc: 'Proaktiv övervakning, underhåll och teknisk support som säkerställer att era system alltid fungerar optimalt och utan avbrott.',
  },
  {
    accent: '244, 114, 182',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
        <line x1="14" y1="4" x2="10" y2="20" />
      </svg>
    ),
    title: 'Utveckling',
    desc: 'Skräddarsydd mjukvaruutveckling med moderna ramverk och metoder. Från webbappar till API-integrationer och automatisering.',
  },
];

const TECHNOLOGIES = [
  'Microsoft Azure',
  'Microsoft 365',
  'Entra ID',
  'Windows Server',
  'Microsoft Intune',
  'VMware',
  'Veeam',
  'Fortinet',
];

const PROCESS = [
  {
    num: '01',
    title: 'Kartläggning',
    desc: 'Vi börjar med att förstå er verksamhet, era system och vart ni är på väg. Inget säljsnack — bara frågor och lyssnande.',
  },
  {
    num: '02',
    title: 'Plan & förslag',
    desc: 'Ni får en tydlig plan med prioriteringar, tidplan och kostnad. Ni vet exakt vad ni får innan vi börjar.',
  },
  {
    num: '03',
    title: 'Genomförande',
    desc: 'Vi bygger, migrerar och driftsätter stegvis med minimal påverkan på er vardag och löpande avstämningar.',
  },
  {
    num: '04',
    title: 'Förvaltning',
    desc: 'Löpande drift, övervakning och support. Vi finns kvar när projektet är klart och ser till att allt fortsätter fungera.',
  },
];

const WHY_US = [
  {
    num: '01',
    title: 'Erfarenhet & expertis',
    desc: 'Vi har gedigen erfarenhet inom IT-branschen och håller oss ständigt uppdaterade med den senaste tekniken.',
  },
  {
    num: '02',
    title: 'Pålitlig partner',
    desc: 'Vi bygger långsiktiga relationer och finns alltid tillgängliga när ni behöver oss. Er framgång är vår framgång.',
  },
  {
    num: '03',
    title: 'Skräddarsytt',
    desc: 'Varje företag är unikt. Vi anpassar våra tjänster efter era specifika behov och förutsättningar.',
  },
  {
    num: '04',
    title: 'Proaktiv support',
    desc: 'Vi väntar inte på att problem uppstår — vi förebygger dem. Löpande övervakning och underhåll ingår.',
  },
];

/* ════════════════════════════ APP ════════════════════════════ */
function App() {
  const { scrolled, progress } = useScrollState();
  const activeSection = useActiveSection(SECTION_IDS);
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  /* Lock body scroll + allow Escape while the mobile menu is open */
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const navLink = (href, label) => {
    const id = href.slice(1);
    return (
      <li key={href}>
        <a
          href={href}
          className={activeSection === id ? 'active' : undefined}
          onClick={closeMenu}
        >
          {label}
        </a>
      </li>
    );
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  return (
    <div className="app">
      {/* Grain overlay */}
      <div className="grain" aria-hidden="true" />

      {/* Reading progress */}
      <div
        className="scroll-progress"
        style={{ transform: `scaleX(${progress})` }}
        aria-hidden="true"
      />

      <a href="#main" className="skip-link">Hoppa till innehållet</a>

      {/* ── Navbar ── */}
      <nav className={`navbar${scrolled ? ' scrolled' : ''}`}>
        <div className="nav-container">
          <a href="#hero" className="nav-logo" onClick={closeMenu}>
            MR<span>IT</span>
          </a>
          <button
            className={`nav-hamburger${menuOpen ? ' open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Stäng meny' : 'Öppna meny'}
            aria-expanded={menuOpen}
          >
            <span /><span /><span />
          </button>
          <ul className={`nav-links${menuOpen ? ' open' : ''}`}>
            {navLink('#about', 'Om oss')}
            {navLink('#services', 'Tjänster')}
            {navLink('#process', 'Arbetssätt')}
            {navLink('#stats', 'Resultat')}
            {navLink('#why-us', 'Varför oss')}
            <li>
              <a href="#contact" className="nav-cta" onClick={closeMenu}>
                Kontakt
              </a>
            </li>
          </ul>
        </div>
      </nav>
      <div
        className={`nav-backdrop${menuOpen ? ' show' : ''}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      <main id="main" tabIndex={-1}>
        {/* ── Hero ── */}
        <section id="hero" className="hero">
          <div className="hero-grid" aria-hidden="true" />
          <div className="hero-glow" aria-hidden="true" />
          <div className="hero-glow hero-glow--2" aria-hidden="true" />
          <div className="hero-glow hero-glow--3" aria-hidden="true" />
          <div className="hero-content">
            <Reveal>
              <p className="hero-pill">
                <span className="pulse-dot" aria-hidden="true" />
                IT-konsulting &middot; Moln &middot; Drift
              </p>
            </Reveal>
            <Reveal delay={120}>
              <h1>
                Vi bygger framtidens<br />
                <span className="gradient-text">digitala infrastruktur</span>
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <p className="hero-desc">
                MR IT AB levererar skräddarsydda IT-lösningar som driver tillväxt.
                Från strategisk rådgivning till molnmigrering och driftsäkerhet
                — vi är den tekniska partnern som moderna företag förtjänar.
              </p>
            </Reveal>
            <Reveal delay={360}>
              <div className="hero-actions">
                <a href="#contact" className="btn btn-glow">Boka ett samtal</a>
                <a href="#services" className="btn btn-ghost">Våra tjänster</a>
              </div>
            </Reveal>
          </div>
          <div className="hero-scroll" aria-hidden="true">
            <span />
          </div>
        </section>

        {/* ── Technology strip ── */}
        <div className="marquee-band">
          <p className="marquee-label">Tekniker vi arbetar med dagligen</p>
          <div className="marquee">
            {[0, 1].map((track) => (
              <div className="marquee-track" key={track} aria-hidden={track === 1}>
                {TECHNOLOGIES.map((tech) => (
                  <span className="marquee-item" key={tech}>{tech}</span>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* ── About ── */}
        <section id="about" className="section section--dark">
          <div className="container">
            <Reveal>
              <p className="section-tag">Om MR IT AB</p>
              <h2 className="section-title">
                Teknik med <span className="gradient-text">mänskligt fokus</span>
              </h2>
            </Reveal>
            <div className="about-grid">
              <Reveal delay={100}>
                <div className="about-image-wrapper">
                  <img
                    src={process.env.PUBLIC_URL + '/images/background.jpg'}
                    alt="MR IT AB — vårt team"
                    className="about-image"
                    loading="lazy"
                  />
                  <div className="about-image-border" aria-hidden="true" />
                  <div className="about-image-glow" aria-hidden="true" />
                </div>
              </Reveal>
              <div className="about-text">
                <Reveal delay={200}>
                  <p className="about-lead">
                    Vi tror att rätt teknik förändrar allt. Sedan starten har MR IT AB
                    hjälpt företag att ta kontroll över sin digitala framtid — med
                    lösningar som är lika pålitliga som de är innovativa.
                  </p>
                </Reveal>
                <Reveal delay={300}>
                  <p>
                    Vårt team har bred erfarenhet inom allt från infrastruktur och
                    molntjänster till systemutveckling och integration. Vi arbetar nära
                    våra kunder för att förstå deras unika behov och leverera lösningar
                    som skapar verkligt värde.
                  </p>
                </Reveal>
                <Reveal delay={400}>
                  <p>
                    Vår filosofi är enkel: teknik ska göra vardagen enklare. Vi tror på
                    långsiktiga partnerskap, transparens och att alltid leverera kvalitet.
                  </p>
                </Reveal>
                <Reveal delay={500}>
                  <div className="about-badges">
                    <span className="badge">Microsoft Partner</span>
                    <span className="badge">Azure Certified</span>
                    <span className="badge">ISO 27001</span>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        {/* ── Services ── */}
        <section id="services" className="section section--darker">
          <div className="container">
            <Reveal>
              <p className="section-tag">Våra tjänster</p>
              <h2 className="section-title">
                Allt ni behöver under <span className="gradient-text">ett tak</span>
              </h2>
              <p className="section-subtitle">
                Från strategisk rådgivning till daglig drift — vi täcker hela spektrumet
                av moderna IT-behov.
              </p>
            </Reveal>
            <div className="services-grid">
              {SERVICES.map((svc, i) => (
                <Reveal key={svc.title} delay={i * 100}>
                  <div
                    className="service-card spot"
                    style={{ '--card-accent': svc.accent }}
                    onMouseMove={trackSpotlight}
                  >
                    <div className="service-icon">{svc.icon}</div>
                    <h3>{svc.title}</h3>
                    <p>{svc.desc}</p>
                    <div className="service-shine" aria-hidden="true" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── Process ── */}
        <section id="process" className="section section--dark">
          <div className="container">
            <Reveal>
              <p className="section-tag">Så jobbar vi</p>
              <h2 className="section-title">
                Från första samtal till <span className="gradient-text">löpande drift</span>
              </h2>
              <p className="section-subtitle">
                Ett tydligt upplägg i fyra steg — så att ni alltid vet var i processen
                vi befinner oss och vad som händer härnäst.
              </p>
            </Reveal>
            <div className="process-grid">
              <div className="process-line" aria-hidden="true" />
              {PROCESS.map((step, i) => (
                <Reveal key={step.num} delay={i * 120} className="process-cell">
                  <div className="process-step spot" onMouseMove={trackSpotlight}>
                    <span className="process-num">{step.num}</span>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── Stats ── */}
        <section id="stats" className="section section--stats">
          <div className="stats-glow" aria-hidden="true" />
          <div className="container">
            <Reveal>
              <p className="section-tag">I siffror</p>
              <h2 className="section-title">
                Bevisad <span className="gradient-text">leveransförmåga</span>
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <div className="stats-panel">
                <StatItem value={10} suffix="+" label="Nöjda kunder" />
                <StatItem value={20} suffix="+" label="År i branschen" />
                <StatItem value={20} suffix="+" label="Avslutade projekt" />
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── Why us ── */}
        <section id="why-us" className="section section--dark">
          <div className="container">
            <Reveal>
              <p className="section-tag">Varför MR IT AB</p>
              <h2 className="section-title">
                Det som skiljer oss <span className="gradient-text">från mängden</span>
              </h2>
            </Reveal>
            <div className="why-grid">
              {WHY_US.map((item, i) => (
                <Reveal key={item.num} delay={i * 120}>
                  <div className="why-card spot" onMouseMove={trackSpotlight}>
                    <span className="why-ghost" aria-hidden="true">{item.num}</span>
                    <span className="why-num">{item.num}</span>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── Contact ── */}
        <section id="contact" className="section section--contact">
          <div className="contact-glow" aria-hidden="true" />
          <div className="container">
            <Reveal>
              <p className="section-tag">Kontakta oss</p>
              <h2 className="section-title">
                Redo att ta nästa <span className="gradient-text">steg?</span>
              </h2>
              <p className="section-subtitle">
                Oavsett om ni behöver strategisk IT-rådgivning, teknisk support eller
                hjälp med molnmigrering — vi finns här för er.
              </p>
            </Reveal>
            <div className="contact-grid">
              <Reveal delay={100}>
                <div className="contact-card spot" onMouseMove={trackSpotlight}>
                  <div className="contact-card-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <h3>E-post</h3>
                  <a href="mailto:info@mr-it.se">info@mr-it.se</a>
                </div>
              </Reveal>
              <Reveal delay={200}>
                <div className="contact-card spot" onMouseMove={trackSpotlight}>
                  <div className="contact-card-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </div>
                  <h3>Telefon</h3>
                  <a href="tel:+46702426411">070-242 64 11</a>
                </div>
              </Reveal>
              <Reveal delay={300}>
                <div className="contact-card spot" onMouseMove={trackSpotlight}>
                  <div className="contact-card-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <h3>Plats</h3>
                  <p>Stockholm, Sverige</p>
                </div>
              </Reveal>
              <Reveal delay={400}>
                <a
                  href="https://www.linkedin.com/in/magnus-rusk-89a77a14/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-card contact-card--link spot"
                  onMouseMove={trackSpotlight}
                >
                  <div className="contact-card-icon contact-card-icon--linkedin">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </div>
                  <h3>LinkedIn</h3>
                  <span className="contact-card-label">Magnus Rusk</span>
                </a>
              </Reveal>
            </div>
            <Reveal delay={400}>
              <div className="contact-bottom">
                <p>Boka ett kostnadsfritt inledande samtal och låt oss diskutera hur vi kan hjälpa ert företag.</p>
                <a href="mailto:info@mr-it.se" className="btn btn-glow">Skicka e-post</a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <a href="#hero" className="nav-logo footer-logo">
                MR<span>IT</span>
              </a>
              <p>
                Professionella IT-lösningar för moderna företag.
                Vi hjälper er att växa med rätt teknik.
              </p>
            </div>
            <div className="footer-col">
              <h4>Navigering</h4>
              <ul>
                <li><a href="#about">Om oss</a></li>
                <li><a href="#services">Tjänster</a></li>
                <li><a href="#process">Arbetssätt</a></li>
                <li><a href="#stats">Resultat</a></li>
                <li><a href="#why-us">Varför oss</a></li>
                <li><a href="#contact">Kontakt</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Tjänster</h4>
              <ul>
                <li><a href="#services">IT-konsulting</a></li>
                <li><a href="#services">Systemintegration</a></li>
                <li><a href="#services">Molnlösningar</a></li>
                <li><a href="#services">Cybersäkerhet</a></li>
                <li><a href="#services">Support & drift</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Kontakt</h4>
              <ul>
                <li><a href="mailto:info@mr-it.se">info@mr-it.se</a></li>
                <li><a href="tel:+46702426411">070-242 64 11</a></li>
                <li><span>Stockholm, Sverige</span></li>
                <li>
                  <a href="https://www.linkedin.com/in/magnus-rusk-89a77a14/" target="_blank" rel="noopener noreferrer" className="footer-linkedin">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
                    LinkedIn
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 MR IT AB. Alla rättigheter förbehållna.</p>
          </div>
        </div>
      </footer>

      {/* ── Back to top ── */}
      <button
        className={`to-top${progress > 0.08 ? ' show' : ''}`}
        onClick={scrollToTop}
        aria-label="Tillbaka till toppen"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
    </div>
  );
}

export default App;
